/**
 * MorrowOS native sub-agent observability.
 *
 * The MorrowOS ReAct engine (apps/server/src/morrow/morrowAgent.ts) spawns
 * sub-agents whose tool calls stream as canonical `item.started` /
 * `item.completed` runtime events carrying a `payload.agentId` (label like
 * `subagent-xxxxxxxx`), plus `agentTask` (the sub-agent's focused task) and
 * `agentModel` (the cheaper model it runs on). Server ingestion projects those
 * into persisted `tool.started` / `tool.updated` / `tool.completed` thread
 * activities that keep the `agentId` / `agentTask` / `agentModel` fields.
 *
 * This module is deliberately independent of the T3 `task.*`-based
 * orchestration subagent fold (subagentRuntime.ts): MorrowOS emits NO `task.*`
 * activities, so that fold never sees these agents. Here we fold the
 * `agentId`-attributed `tool.*` activities directly into:
 *   - a timeline-ready roster of "agent cards" (label + status + one-line
 *     summary of what each is doing), and
 *   - a per-agent detail (ordered tool calls with name, input, result, status)
 *     the right panel renders when a card is clicked.
 *
 * Pure — memoize by activity-list identity at the caller.
 */
import type { OrchestrationThreadActivity } from "@t3tools/contracts";

export type MorrowAgentStatus = "running" | "completed" | "failed";

/**
 * Agent id the engine stamps on the top-level orchestrator's own reasoning
 * cards (mirrors the server's `MAIN_AGENT_ID`). The UI labels this "Morrow" and
 * treats it as the planner rather than a spawned sub-agent.
 */
export const MORROW_MAIN_AGENT_ID = "main";

/** Fixed display name for spawned MorrowOS sub-agents in the timeline and panel. */
export const MORROW_SUBAGENT_DISPLAY_LABEL = "Morb";

/** Display name for an agent card — the orchestrator reads as "Morrow", sub-agents as "Morb". */
export function morrowAgentDisplayLabel(agent: Pick<MorrowAgent, "agentId" | "label">): string {
  return agent.agentId === MORROW_MAIN_AGENT_ID ? "Morrow" : MORROW_SUBAGENT_DISPLAY_LABEL;
}

export interface MorrowAgentToolCall {
  /** Stable id: the owning tool activity's event id (started, else completed). */
  readonly id: string;
  readonly toolName: string;
  readonly status: "running" | "completed" | "failed";
  readonly input: unknown;
  readonly result: unknown;
  readonly startedAt: string;
  readonly completedAt: string | null;
}

/**
 * A sub-agent's own text: interim reasoning between tool calls, or its final
 * consolidated answer (`role: "answer"`). Folded from the engine's `agent_note`
 * rows so the card can show what the agent thought and concluded — separately
 * from the user-facing reply.
 */
export interface MorrowAgentNote {
  readonly id: string;
  readonly role: "reasoning" | "answer";
  readonly text: string;
  readonly at: string;
}

export interface MorrowAgent {
  readonly agentId: string;
  /**
   * The turn this agent's activity belongs to. The roster is keyed by
   * (turnId, agentId) so a turn's cards render only under that turn — the
   * "main" narrator, in particular, must not accumulate every turn's notes.
   */
  readonly turnId: string | null;
  /** Short label shown on the card (e.g. "Morb" for spawned sub-agents). */
  readonly label: string;
  /** The sub-agent's focused task (the one-line "what it's doing" summary). */
  readonly task: string | null;
  /** The cheaper model this sub-agent runs on, when known. */
  readonly model: string | null;
  readonly status: MorrowAgentStatus;
  readonly toolCalls: ReadonlyArray<MorrowAgentToolCall>;
  /** The sub-agent's reasoning trail + final answer, in order. */
  readonly notes: ReadonlyArray<MorrowAgentNote>;
  /** The sub-agent's consolidated final answer (last `answer` note), if any. */
  readonly finalAnswer: string | null;
  /** Toolkits the agent asked the user to connect (inline connect cards). */
  readonly connectRequests: ReadonlyArray<string>;
  /** The tool the agent is currently / was last running. */
  readonly currentToolName: string | null;
  readonly firstSeenAt: string;
  readonly updatedAt: string;
}

export interface MorrowAgentPanelModel {
  readonly agents: ReadonlyArray<MorrowAgent>;
  readonly runningCount: number;
  readonly hasAgents: boolean;
}

const EMPTY_MODEL: MorrowAgentPanelModel = {
  agents: [],
  runningCount: 0,
  hasAgents: false,
};

export function emptyMorrowAgentPanelModel(): MorrowAgentPanelModel {
  return EMPTY_MODEL;
}

const TOOL_KINDS: ReadonlySet<string> = new Set(["tool.started", "tool.updated", "tool.completed"]);

/** True when a thread activity is a MorrowOS sub-agent-attributed tool row. */
export function isMorrowAgentActivity(activity: OrchestrationThreadActivity): boolean {
  if (!TOOL_KINDS.has(activity.kind)) {
    return false;
  }
  if (typeof activity.payload !== "object" || activity.payload === null) {
    return false;
  }
  const agentId = (activity.payload as Record<string, unknown>).agentId;
  return typeof agentId === "string" && agentId.trim().length > 0;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

/** Strip the engine's "[subagent] " prefix off a tool title. */
function cleanToolName(raw: string | undefined, dataToolName: string | undefined): string {
  const fromData = asString(dataToolName);
  if (fromData) {
    return fromData;
  }
  const title = asString(raw) ?? "tool";
  return title.replace(/^\[subagent\]\s*/i, "").replace(/\s+started$/i, "");
}

interface MutableToolCall {
  id: string;
  toolName: string;
  status: "running" | "completed" | "failed";
  input: unknown;
  result: unknown;
  startedAt: string;
  completedAt: string | null;
}

interface MutableAgent {
  agentId: string;
  turnId: string | null;
  task: string | null;
  model: string | null;
  status: MorrowAgentStatus;
  firstSeenAt: string;
  updatedAt: string;
  /** Tool calls keyed by their runtime item id (data.toolCallId / event id). */
  byCallId: Map<string, MutableToolCall>;
  order: string[];
  /** Reasoning/answer notes in arrival order, deduped by activity id. */
  notes: MorrowAgentNote[];
  noteIds: Set<string>;
  /** Toolkits the agent asked the user to connect, deduped. */
  connectRequests: string[];
}

/**
 * Fold a thread's activities into the MorrowOS sub-agent roster. Tolerant:
 * malformed rows are skipped. A `tool.started` opens a running call; a matching
 * `tool.completed` closes it (by tool-call id when present, else the most
 * recent open call for the same tool). Agents with any open call read as
 * running; otherwise they inherit their last call's terminal status.
 */
export function foldMorrowAgents(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  options?: {
    /** The turn id currently in progress, if any (from the chat view). */
    readonly activeTurnId?: string | null;
    /** True while that turn has not yet settled — agents in it stay "working". */
    readonly turnActive?: boolean;
  },
): ReadonlyArray<MorrowAgent> {
  const agents = new Map<string, MutableAgent>();

  for (const activity of activities) {
    if (!isMorrowAgentActivity(activity)) {
      continue;
    }
    const payload = activity.payload as Record<string, unknown>;
    const agentId = asString(payload.agentId);
    if (!agentId) {
      continue;
    }
    const at = activity.createdAt;
    const data =
      typeof payload.data === "object" && payload.data !== null
        ? (payload.data as Record<string, unknown>)
        : null;
    const toolName = cleanToolName(
      typeof payload.title === "string" ? payload.title : undefined,
      data && typeof data.toolName === "string" ? data.toolName : undefined,
    );
    const callId = asString(data?.toolCallId) ?? asString(payload.toolCallId) ?? activity.id;

    // Scope the roster entry to (turnId, agentId). Without the turnId the single
    // "main" agent id would collect every turn's reasoning notes, and re-used
    // sub-agent ids would bleed across turns — the stale-cascade bug.
    const turnId = activity.turnId ?? null;
    const rosterKey = `${turnId ?? " "}::${agentId}`;

    let agent = agents.get(rosterKey);
    if (!agent) {
      agent = {
        agentId,
        turnId,
        task: asString(payload.agentTask) ?? null,
        model: asString(payload.agentModel) ?? null,
        status: "running",
        firstSeenAt: at,
        updatedAt: at,
        byCallId: new Map(),
        order: [],
        notes: [],
        noteIds: new Set(),
        connectRequests: [],
      };
      agents.set(rosterKey, agent);
    }
    // Metadata fill (never downgrade known values).
    if (agent.task === null) agent.task = asString(payload.agentTask) ?? null;
    if (agent.model === null) agent.model = asString(payload.agentModel) ?? null;
    agent.updatedAt = at;

    // A connect-app request — captured as a toolkit the user should connect,
    // not a tool call.
    if (data && data.kind === "connect_request") {
      const toolkit = asString(data.toolkit);
      if (toolkit && !agent.connectRequests.includes(toolkit)) {
        agent.connectRequests.push(toolkit);
      }
      continue;
    }

    // A sub-agent note (reasoning/answer) — captured into the agent's own text,
    // not its tool-call list.
    if (data && data.kind === "agent_note") {
      const noteText = asString(data.noteText);
      if (noteText && !agent.noteIds.has(activity.id)) {
        agent.noteIds.add(activity.id);
        agent.notes.push({
          id: activity.id,
          role: data.noteRole === "answer" ? "answer" : "reasoning",
          text: noteText,
          at,
        });
      }
      continue;
    }

    if (activity.kind === "tool.started") {
      if (!agent.byCallId.has(callId)) {
        agent.byCallId.set(callId, {
          id: callId,
          toolName,
          status: "running",
          input: data?.input,
          result: undefined,
          startedAt: at,
          completedAt: null,
        });
        agent.order.push(callId);
      }
      continue;
    }

    // tool.updated / tool.completed: find the target call (by id, else the most
    // recent open call for this tool, else create one for a lone completion).
    let call = agent.byCallId.get(callId);
    if (!call) {
      for (let index = agent.order.length - 1; index >= 0; index -= 1) {
        const candidate = agent.byCallId.get(agent.order[index]!);
        if (candidate && candidate.status === "running" && candidate.toolName === toolName) {
          call = candidate;
          break;
        }
      }
    }
    if (!call) {
      call = {
        id: callId,
        toolName,
        status: "running",
        input: data?.input,
        result: undefined,
        startedAt: at,
        completedAt: null,
      };
      agent.byCallId.set(callId, call);
      agent.order.push(callId);
    }
    if (data?.input !== undefined && call.input === undefined) call.input = data.input;
    if (data?.result !== undefined) call.result = data.result;

    if (activity.kind === "tool.completed") {
      const status = payload.status;
      call.status = status === "failed" ? "failed" : "completed";
      call.completedAt = at;
    }
  }

  const roster: MorrowAgent[] = [];
  for (const agent of agents.values()) {
    const toolCalls = agent.order
      .map((id) => agent.byCallId.get(id))
      .filter((call): call is MutableToolCall => call !== undefined)
      .map((call) => ({ ...call }));
    const anyRunning = toolCalls.some((call) => call.status === "running");
    const lastTerminal = [...toolCalls].reverse().find((call) => call.status !== "running");
    // Only a genuine "answer" note is the consolidated answer. (The main
    // orchestrator emits reasoning notes but no answer note — its final reply is
    // the user-facing message, not a card — so it must not fall back to a
    // reasoning note here.)
    const finalAnswer =
      [...agent.notes].reverse().find((note) => note.role === "answer")?.text ?? null;
    // Status must NOT collapse to "completed" during the gaps where no tool is
    // in flight — reasoning before the first tool, between tools, or while
    // awaiting a spawned child. That inference caused the "working → gone →
    // working" flicker. An agent stays "running" until a genuine terminal
    // signal: a sub-agent delivers its `answer` note; the "main" orchestrator
    // (which emits no answer note) is done only once its turn settles.
    const inActiveTurn =
      options?.turnActive === true &&
      agent.turnId != null &&
      agent.turnId === (options?.activeTurnId ?? null);
    const status: MorrowAgentStatus = anyRunning
      ? "running"
      : lastTerminal?.status === "failed"
        ? "failed"
        : finalAnswer
          ? "completed"
          : inActiveTurn
            ? "running"
            : "completed";
    const currentTool = anyRunning
      ? [...toolCalls].reverse().find((call) => call.status === "running")
      : toolCalls[toolCalls.length - 1];
    roster.push({
      agentId: agent.agentId,
      turnId: agent.turnId,
      label: morrowAgentDisplayLabel({ agentId: agent.agentId, label: "" }),
      task: agent.task,
      model: agent.model,
      status,
      toolCalls,
      notes: agent.notes.map((note) => ({ ...note })),
      finalAnswer,
      connectRequests: [...agent.connectRequests],
      currentToolName: currentTool?.toolName ?? null,
      firstSeenAt: agent.firstSeenAt,
      updatedAt: agent.updatedAt,
    });
  }
  // Stable spawn order.
  roster.sort(
    (a, b) => a.firstSeenAt.localeCompare(b.firstSeenAt) || a.agentId.localeCompare(b.agentId),
  );
  return roster;
}

export function deriveMorrowAgentPanelModel(
  agents: ReadonlyArray<MorrowAgent>,
): MorrowAgentPanelModel {
  if (agents.length === 0) {
    return EMPTY_MODEL;
  }
  let runningCount = 0;
  for (const agent of agents) {
    if (agent.status === "running") runningCount += 1;
  }
  return { agents, runningCount, hasAgents: true };
}

/** Compact one-line "doing" summary for an agent card. */
export function morrowAgentSummary(agent: MorrowAgent): string {
  if (agent.status === "running") {
    return agent.currentToolName ? `Running ${agent.currentToolName}…` : "Working…";
  }
  if (agent.status === "failed") {
    return agent.task ?? "Failed";
  }
  const doneCount = agent.toolCalls.length;
  return agent.task ?? (doneCount > 0 ? `${doneCount} tool calls` : "Done");
}

/** Compact input/result preview for a tool-call row in the panel. */
export function previewToolValue(value: unknown, limit = 200): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  let text: string;
  if (typeof value === "string") {
    text = value;
  } else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }
  text = text.replace(/\s+/g, " ").trim();
  if (text.length === 0) {
    return null;
  }
  return text.length <= limit ? text : `${text.slice(0, limit - 1)}…`;
}
